import React from "react";
import classNames from "classnames";
import { Box, Tab, TabProps, Tabs, TabsProps } from "@mui/material";

import { TAB_ID } from "App/routes/Settings";
import { useMobileAppParams } from "App/routes/Main/useMobileAppParams";
import styles from "./TabbedViewV2.module.scss";

export type TabConfig = {
  id: string;
  name: string;
  onClick: () => void;
  view?: React.ReactNode;
  keepOnDismount?: boolean;
};

interface TabbedViewV2Props {
  tabs: TabConfig[];
  activeTabId: string;
  hideTabBar?: boolean;
  ariaLabel?: string;
  tabsProps?: TabsProps;
  tabProps?: TabProps;
  disableStickyTabs?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  activeValue: string;
  "data-active"?: "active" | "inactive";
}

function TabPanel(props: TabPanelProps) {
  const { children, value, activeValue, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== activeValue}
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      className={styles.tabPanel}
      {...other}
    >
      {value === activeValue && children}
    </div>
  );
}

function a11yProps(id: string) {
  return {
    id: `tab-${id}`,
    "aria-controls": `tabpanel-${id}`,
  };
}

export const TabbedViewV2 = ({
  tabs,
  activeTabId,
  hideTabBar,
  tabsProps,
  tabProps,
  ariaLabel = "Navigation tabs",
  disableStickyTabs,
}: TabbedViewV2Props) => {
  const { isAuthorizedMobileComponent } = useMobileAppParams();

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    const tab = tabs.find((t) => t.id === newValue);
    if (tab) {
      tab.onClick();
    }
  };

  const filteredTabs = tabs.filter(
    (t) =>
      !(
        (t.id === TAB_ID.NOTIFICATIONS || t.id === TAB_ID.PLANS) &&
        isAuthorizedMobileComponent
      ),
  );

  return (
    <Box className={styles.root}>
      {!hideTabBar && (
        <Box
          className={classNames(
            styles.tabListContainer,
            !disableStickyTabs && styles.sticky,
          )}
        >
          <Tabs
            {...tabsProps}
            disableRipple
            value={activeTabId}
            onChange={tabsProps?.onChange || handleChange}
            aria-label={tabsProps?.["aria-label"] || ariaLabel}
            className={classNames(styles.tabList, tabsProps?.className)}
            selectionFollowsFocus={tabsProps?.selectionFollowsFocus || true}
            TabIndicatorProps={{
              ...tabsProps?.TabIndicatorProps,
              className:
                tabsProps?.TabIndicatorProps?.className || styles.tabIndicator,
            }}
          >
            {filteredTabs.map((tab) => (
              <Tab
                {...tabProps}
                key={tab.id}
                label={tabProps?.label || tab.name}
                value={tab.id}
                className={classNames(styles.tab, tabProps?.className)}
                data-cypress-id="highnote-tab"
                {...a11yProps(tab.id)}
                disableRipple
              />
            ))}
          </Tabs>
        </Box>
      )}
      {filteredTabs.map((tab) => {
        if (!tab.keepOnDismount && tab.id !== activeTabId) {
          return null;
        }
        return (
          <TabPanel
            key={tab.id}
            value={tab.id}
            activeValue={activeTabId}
            data-active={tab.id === activeTabId ? "active" : "inactive"}
          >
            {tab.view || null}
          </TabPanel>
        );
      })}
    </Box>
  );
};
